import { createEventsResultsApi } from "#ce/apps/SpFinder/api/events-results-api";
import { createEventsHistory } from "#ce/apps/SpFinder/routing/events-history";
import { decodeFinderParams } from "#ce/apps/SpFinder/routing/finder-url-codec";
import { createRequestLifecycle } from "./request-lifecycle";

const criteriaNames = new Set([
  "query",
  "categories",
  "categories[]",
  "locations",
  "locations[]",
  "near",
  "lat",
  "lng",
  "between",
  "distance",
  "sort",
]);

/** Returns nonblank events criteria, preserving repeated parameter values. */
export function eventsCriteria(searchParams) {
  return Object.fromEntries(
    Object.entries(decodeFinderParams(searchParams)).filter(
      ([name, value]) => criteriaNames.has(name) && [value].flat().some((item) => item !== ""),
    ),
  );
}

/** Creates a coalesced events request service with cancellation, history, and teardown. */
export function createEventsSearch({
  publish,
  render,
  reportRequest,
  reportCompletion,
  reportError,
  fetch,
  ...options
}) {
  const history = createEventsHistory(options);
  let pending;
  let timer;
  let closed = false;
  let loading = false;
  let activeOptions;
  let searchCount = 0;
  const lifecycle = createRequestLifecycle({
    api: createEventsResultsApi({ fetch }),
    onRequest: reportRequest,
    onComplete: complete,
    onError: fail,
  });
  const stopHistory = history.listen(restore);

  function complete(completion) {
    loading = false;
    reportCompletion?.(completion);
    if (activeOptions.updateAddressBar !== false) {
      try {
        history.commit(completion.url);
      } catch (error) {
        console.error(error);
      }
    }
    render(completion.result, activeOptions);
  }

  function fail({ error, ...request }) {
    loading = false;
    reportError?.({ ...request, error: { name: error.name, message: error.message } });
  }

  function initialize() {
    if (!closed) {
      publish(eventsCriteria(history.current().searchParams));
    }
  }

  function flush() {
    timer = undefined;
    const command = pending;
    pending = undefined;
    if (closed || !command) {
      return;
    }
    const destination = history.destination(command.value);
    const params = eventsCriteria(destination.searchParams);
    activeOptions = command.options;
    publish(params);
    loading = true;
    lifecycle.request(destination.toString(), params);
  }

  function search(value, commandOptions = {}) {
    if (closed) {
      return;
    }
    searchCount += 1;
    lifecycle.cancel();
    clearTimeout(timer);
    pending = { value, options: { refreshUI: true, scrollIntoView: false, ...commandOptions } };
    timer = setTimeout(flush, 1);
  }

  // Cancels the request in flight and any search about to start, and returns the search count it claims.
  // A caller that waits before searching compares it with `searchCount` to tell whether a newer search
  // arrived meanwhile.
  function claim() {
    if (closed) {
      return undefined;
    }
    searchCount += 1;
    lifecycle.cancel();
    clearTimeout(timer);
    pending = undefined;
    return searchCount;
  }

  function restore(value) {
    search(value, { updateAddressBar: false, refreshUI: true, scrollIntoView: true });
  }

  function dispose() {
    closed = true;
    clearTimeout(timer);
    pending = undefined;
    loading = false;
    stopHistory();
    lifecycle.dispose();
  }
  return Object.freeze({
    search,
    claim,
    restore,
    initialize,
    dispose,
    currentUrl: history.current,
    get loading() {
      return loading;
    },
    /** The number of searches asked for, so a caller that waits can tell whether a newer one arrived. */
    get searchCount() {
      return searchCount;
    },
  });
}
